import React, { FC, useCallback, useContext, useState } from "react";
import { Button } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp } from "./BasicIntegrationCard";
import { LeadConnectDialogContainer } from "../containers/LeadConnectDialogContainer";
import { LEAD_CONNECT_NAME } from "../integrationCatalog";
import { IntegrationCardLayoutContext } from "../integrationCardContext";

export interface LeadConnectIntegrationCardProps extends IntegrationCardProp {
    organizationId: string;
    /** The integration or add-on changed: refetch the integrations page. */
    onChanged?(): void;
    /** The add-on's status: active, cancelled, or absent when never bought. */
    addOnStatus?: string | null;
    /** admin when an admin granted the add-on rather than the business buying it. */
    addOnSource?: string | null;
}

/**
 * What removing Lead Connect does, from its add-on: a bought add-on is cancelled behind a question,
 * since dropping only the setup would leave the business paying; an admin grant has no remove; with
 * no active add-on it only drops the setup.
 */
export function leadConnectRemoval(status?: string | null, source?: string | null): "cancel" | "none" | "disconnect" {
    if (status !== "active") {
        return "disconnect";
    }
    return source === "admin" ? "none" : "cancel";
}

/**
 * Lead Connect, a paid add-on. Clicking the card buys it or sets it up; once set up, "Lead
 * Connect Status" shows the number, places a test call and cancels the add-on.
 *
 * Removing a bought add-on from the card cancels it, behind the same question as the dialog:
 * dropping only the setup would leave the business paying. A granted add-on has no remove.
 *
 * The dialog's container is mounted only while it is open, so its queries run only then without
 * pausing a query on the dialog's open state.
 */
export const LeadConnectIntegrationCard: FC<LeadConnectIntegrationCardProps> = (props) => {
    const { appId, organizationId, checked, disabled, isLinked, isActive, onDisconnect, onChanged } = props;
    const { addOnStatus, addOnSource } = props;

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [cancelling, setCancelling] = useState<boolean>(false);

    const removal = leadConnectRemoval(addOnStatus, addOnSource);
    const bought = removal === "cancel";

    const handleRemove = useCallback(() => {
        if (bought) {
            setCancelling(true);
            setShowDialog(true);
        } else if (typeof onDisconnect === "function") {
            onDisconnect();
        }
    }, [bought, onDisconnect]);

    const handleClose = useCallback(() => {
        setShowDialog(false);
        setCancelling(false);
    }, []);

    const layout = useContext(IntegrationCardLayoutContext);
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>): void => {
            e.preventDefault();

            if (typeof checked === "boolean") {
                if (typeof props.onClick === "function") {
                    props.onClick(e);
                }
            } else if (!disabled) {
                setShowDialog(true);
            }
        },
        [checked, disabled, props.onClick]
    );

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/lead-connect/lead-connect-logo.svg"
                integrationId="leadconnect"
                text={LEAD_CONNECT_NAME}
                href={() => "/integrations/leadconnect"}
                onClick={handleClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={removal === "none" || typeof onDisconnect !== "function" ? undefined : handleRemove}
            />
            {/* The connected row's Settings button opens the same dialog */}
            {isLinked && layout === "tile" && (
                <div style={{ margin: "8px 0 16px" }}>
                    <Button data-lead-connect-status-button variation="secondary" onClick={() => setShowDialog(true)}>
                        {LEAD_CONNECT_NAME} Status
                    </Button>
                </div>
            )}
            {showDialog && (
                <LeadConnectDialogContainer
                    appId={appId}
                    organizationId={organizationId}
                    active={showDialog}
                    onClose={handleClose}
                    onChanged={onChanged}
                    confirmCancelOnOpen={cancelling}
                />
            )}
        </>
    );
};
